import { create } from 'zustand';
import axios from 'axios';
import { API_integrate_heartland_retail } from '../constants';
import { getDefaultAxiosConfig } from '../main/content/requests/utilities/requests';

const useIntegratePOSHeartlandRetailStore = create((set, get) => ({
  // Heartland Retail-specific form data
  formData: {
    // Prerequisites (Step 0)
    heartlandAccountVerified: false,

    // API Setup (Step 1)
    apiSetupCompleted: false,

    // API Credential Values (Step 2)
    secretKey: '',
  },

  // Current step/page
  currentStep: 0,

  // Total steps for Heartland Retail integration
  totalSteps: 3, // Prerequisites, API Setup, Credential Input

  // Actions
  setFormData: (field, value) => set((state) => ({
    formData: {
      ...state.formData,
      [field]: value,
    },
  })),

  // Step 0: Prerequisites validation
  setHeartlandAccountVerified: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      heartlandAccountVerified: completed,
    },
  })),

  // Step 1: API setup validation
  setAPISetupCompleted: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      apiSetupCompleted: completed,
    },
  })),

  // Navigation
  nextStep: () => set((state) => ({
    currentStep: Math.min(state.currentStep + 1, state.totalSteps - 1),
  })),

  previousStep: () => set((state) => ({
    currentStep: Math.max(state.currentStep - 1, 0),
  })),

  resetForm: () => set(() => ({
    formData: {
      heartlandAccountVerified: false,
      apiSetupCompleted: false,
      secretKey: '',
    },
    currentStep: 0,
  })),

  // Validation helpers
  isStepValid: (step) => {
    const { formData } = get();
    switch (step) {
      case 0: // Prerequisites check
        return formData.heartlandAccountVerified;
      case 1: // API setup
        return formData.apiSetupCompleted;
      case 2: // Credential input
        return formData.secretKey.trim().length > 0;
      default:
        return false;
    }
  },

  // Heartland Retail integration function
  integrateHeartlandRetail: async (company_id) => {
    try {
      const { formData, resetForm } = get();

      console.log('Heartland Retail integration completed!');

      // Get axios config with proper authentication
      const axiosConfig = await getDefaultAxiosConfig();

      // Make API call to your backend to save the Heartland Retail credentials
      const response = await axios.post(API_integrate_heartland_retail, {
        company_id,
        integrationType: 'api',
        secret_key: formData.secretKey,
        createdAt: new Date().toISOString(),
        version: '1.0'
      }, axiosConfig);

      const connectionSuccessful = Boolean(
        response?.data?.redux?.payload?.connection_successful
      );

      if (!connectionSuccessful) {
        return {
          success: false,
          error: "POS authentication failed. Please verify your credentials and try again.",
        };
      }

      // Reset the form after successful integration
      resetForm();

      return {
        success: true,
        data: {
          pos: 'heartland-retail',
          integrationType: 'api'
        }
      };

    } catch (error) {
      console.error('Error integrating Heartland Retail:', error);

      const serverMessage = error?.response?.data?.system?.message;

      return {
        success: false,
        error: serverMessage || error.message || 'Failed to integrate Heartland Retail. Please try again.'
      };
    }
  },
}));

export default useIntegratePOSHeartlandRetailStore;
